import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  Input,
  ListBox,
  Pagination,
  Select,
  Spinner,
  Table,
  TextField,
} from '@heroui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Download, Plus, Search, Upload } from 'lucide-react';
import { useWorkersPage } from './useWorkersPage';
import { ImportWorkersModal } from './components/ImportWorkersModal';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import { formatDateTime } from '@/lib/formatDate';
import type { WorkerRow } from '@/lib/types';
import { PageContainer } from '@/components/templates/PageContainer';

const columnHelper = createColumnHelper<WorkerRow>();

/** Раздел «Исполнители»: анкеты воркеров, активность (бейдж), импорт/экспорт. */
export function WorkersPage() {
  const {
    isStaff,
    items,
    total,
    page,
    limit,
    clients,
    clientFilter,
    searchInput,
    isLoading,
    error,
    setSearchInput,
    handleSearchSubmit,
    handleClientFilterChange,
    handlePageChange,
    isImportOpen,
    importResult,
    isImporting,
    handleOpenImport,
    handleCloseImport,
    handleDownloadSample,
    handleExport,
    handleImport,
  } = useWorkersPage();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const cols: ColumnDef<WorkerRow, any>[] = [
      columnHelper.accessor('internalId', {
        header: 'Внутренний ID',
        cell: (info) => info.getValue() ?? '—',
      }),
      // ФИО — опциональное настраиваемое поле, идентифицирует логин
      columnHelper.accessor('login', {
        header: 'Логин',
        cell: (info) => (
          <span className="text-sm font-medium text-foreground">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor((row) => row.workerFields?.phone ?? null, {
        id: 'phone',
        header: 'Телефон',
        cell: (info) => info.getValue() ?? '—',
      }),
    ];
    if (isStaff) {
      cols.push(
        columnHelper.accessor((row) => row.client?.title ?? '—', {
          id: 'client',
          header: 'Клиент',
          cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        }),
      );
    }
    cols.push(
      columnHelper.accessor('pdConsentAcceptedAt', {
        header: 'Согласие на ПД',
        cell: (info) => (
          <span className="text-sm text-muted">{formatDateTime(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor('offerAcceptedAt', {
        header: 'Оферта',
        cell: (info) => (
          <span className="text-sm text-muted">{formatDateTime(info.getValue())}</span>
        ),
      }),
      // в таблице — только статус-бейдж; тумблер на карточке исполнителя
      columnHelper.accessor('enabled', {
        header: 'Статус',
        cell: (info) => (
          <Chip color={info.getValue() ? 'success' : 'danger'} size="sm" variant="soft">
            {info.getValue() ? 'Активен' : 'Неактивен'}
          </Chip>
        ),
      }),
    );
    return cols;
  }, [isStaff]);

  const table = useReactTable({
    columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: -1,
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '…', totalPages] as const;
    if (page >= totalPages - 3)
      return [
        1,
        '…',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ] as const;
    return [1, '…', page - 1, page, page + 1, '…', totalPages] as const;
  }, [totalPages, page]);
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <PageContainer>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-foreground">Исполнители</h2>
        <div className="flex flex-wrap items-end gap-2">
          <Button onPress={() => navigate('/workers/new')}>
            <Plus className="size-4" />
            Создать
          </Button>
          <Button variant="outline" onPress={() => void handleDownloadSample()}>
            <Download className="size-4" />
            Скачать пример
          </Button>
          <Button variant="outline" onPress={handleOpenImport}>
            <Upload className="size-4" />
            Импортировать
          </Button>
          <Button variant="outline" onPress={() => void handleExport()}>
            <Download className="size-4" />
            Экспорт
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchSubmit();
          }}
        >
          <TextField
            className="w-72"
            variant="secondary"
            value={searchInput}
            onChange={setSearchInput}
          >
            <FieldLabel>Поиск</FieldLabel>
            <Input placeholder="ФИО, логин, телефон, внутренний ID" />
          </TextField>
          <Button variant="outline" aria-label="Найти" onPress={handleSearchSubmit}>
            <Search className="size-4" />
          </Button>
        </form>
        {isStaff && (
          <Select
            className="w-64"
            variant="secondary"
            value={clientFilter ?? 'all'}
            onChange={(key) =>
              handleClientFilterChange(key === 'all' || key === null ? null : String(key))
            }
          >
            <FieldLabel>Клиент</FieldLabel>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                <ListBox.Item id="all" textValue="Все клиенты">
                  Все клиенты
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                {clients.map((client) => (
                  <ListBox.Item key={client.id} id={client.id} textValue={client.title}>
                    {client.title}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        )}
      </div>

      {error && (
        <p className="mb-3 rounded-[var(--radius)] bg-danger px-3 py-2 text-sm text-danger-foreground">
          {error}
        </p>
      )}

      <Table className="min-h-[280px]">
        <Table.ScrollContainer>
          <Table.Content aria-label="Список исполнителей" className="min-w-[960px]">
            <Table.Header>
              {table.getHeaderGroups()[0]!.headers.map((header) => (
                <Table.Column key={header.id} id={header.id} isRowHeader={header.id === 'name'}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Table.Column>
              ))}
            </Table.Header>
            <Table.Body
              renderEmptyState={() =>
                isLoading ? (
                  <div className="flex w-full items-center justify-center gap-2 py-10 text-sm text-muted">
                    <Spinner size="sm" />
                    Загрузка…
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-center py-10 text-sm text-muted">
                    Исполнители не найдены
                  </div>
                )
              }
            >
              {table.getRowModel().rows.map((row) => (
                <Table.Row
                  key={row.id}
                  id={row.id}
                  className="cursor-pointer"
                  onAction={() => navigate(`/workers/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
        <Table.Footer>
          <Pagination size="sm">
            <Pagination.Summary>
              {start}–{end} из {total}
            </Pagination.Summary>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={page <= 1}
                  onPress={() => handlePageChange(page - 1)}
                >
                  <Pagination.PreviousIcon />
                  Назад
                </Pagination.Previous>
              </Pagination.Item>
              {pages.map((p, idx) =>
                p === '…' ? (
                  <Pagination.Item key={`ellipsis-${idx}`}>
                    <span className="px-2 text-sm text-muted">…</span>
                  </Pagination.Item>
                ) : (
                  <Pagination.Item key={p}>
                    <Pagination.Link isActive={p === page} onPress={() => handlePageChange(p)}>
                      {p}
                    </Pagination.Link>
                  </Pagination.Item>
                ),
              )}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={page >= totalPages}
                  onPress={() => handlePageChange(page + 1)}
                >
                  Вперёд
                  <Pagination.NextIcon />
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </Table.Footer>
      </Table>

      <ImportWorkersModal
        isOpen={isImportOpen}
        onClose={handleCloseImport}
        isImporting={isImporting}
        importResult={importResult}
        onImport={(file) => void handleImport(file)}
      />
    </PageContainer>
  );
}
